import { inject, Injectable } from '@angular/core';
import {
  EventServiceEvent,
  EventServiceRepository,
  toEventDetails,
} from '@softapps/core/events';
import { AdditionalData, RosterContent } from '@softapps/pms/roster-core';
import {
  RosterEventProvider,
  RosterEventProviderContext,
} from '../types/roster-event-provider';

/** Loads external calendar events (e.g. Outlook) from the event-service per employee UPN. */
@Injectable()
export class EventServiceEventProvider implements RosterEventProvider {
  readonly key = 'event-service';

  private readonly repository = inject(EventServiceRepository);

  async loadEvents(context: RosterEventProviderContext): Promise<RosterContent[]> {
    const events = context?.employee?.upn
      ? await this.repository.load({
          from: context.from,
          to: context.to,
          user: context.employee.upn,
        })
      : await this.repository.loadEventsFromCurrentUser({
          from: context.from,
          to: context.to,
        });

    return (events ?? []).map((event) =>
      this.toRosterContent(event, context.employee.id)
    );
  }

  private toRosterContent(
    event: EventServiceEvent,
    employeeId: number | string
  ): RosterContent {
    const externalDetails = toEventDetails(event);
    return {
      // backend id is null — synthesize a key stable across reloads
      id: `ext:${this.key}:${employeeId}:${event.start}:${event.subject}`,
      title: event.subject,
      start: externalDetails.start,
      end: externalDetails.end,
      allDay: externalDetails.allDay,
      resource: employeeId,
      editable: false,
      resize: false,
      external: true,
      externalDetails,
      additionalData: {
        type: 'APPOINTMENT',
        begintime: externalDetails.start.toISOString(),
        endtime: externalDetails.end.toISOString(),
        comment: event.location ?? '',
        additionaldetail: [],
      } as unknown as AdditionalData,
    };
  }
}
